import {useContext, useMemo} from 'react';
import {get, map, filter} from 'lodash';
import {I18nReactContext} from "../../contexts/I18nContext";
import {useAdminOrganizationsQuery, useUniqueSignDocsQuery} from './useQueryHooks';

/**
 * Custom React hook to fetch and manage user's administrative organizations.
 *
 * REFACTORED: Now uses React Query for caching and automatic refetch.
 * API remains backward-compatible with existing components.
 *
 * @param {object} params - The parameters for the hook.
 * @param {boolean} params.withAllOption - If true, an "All Organizations" option will be added to the organization options list.
 * @param {boolean} [params.withCoursesAndSignDocs] - If true, fetches organizations with their associated courses and sign documents.
 * @param {boolean} [params.includePersonalOrgs=false] - If true, includes VPO (Virtual Personal Organizations) in the results.
 * @returns {object} An object containing the administrative organization data and related utilities.
 * @property {Array<object>} adminOrganizationOptions - A list of organization options suitable for dropdowns.
 * @property {Array<object>} adminOrganizations - The raw list of administrative organizations.
 * @property {boolean} isOrgsLoading - A boolean indicating if the organizations data is currently being loaded.
 * @property {function(): Promise<void>} triggerReloadOrganizations - An async function to manually trigger a reload of the organizations data. Returns a Promise that resolves when all refetches complete.
 */
export const useUserAdminOrgs = ({
    withAllOption,
    withCoursesAndSignDocs,
    includePersonalOrgs = false
}) => {
    const {i18n} = useContext(I18nReactContext);

    // Fetch admin organizations (always needed)
    const {
        data: adminOrgs,
        isLoading: isLoadingOrgs,
        refetch: refetchOrgs
    } = useAdminOrganizationsQuery();

    // Fetch unique SignDocs (only if needed)
    const {
        data: uniqueSignDocs,
        isLoading: isLoadingSignDocs,
        refetch: refetchSignDocs
    } = useUniqueSignDocsQuery({
        includePersonalOrgs,
        enabled: withCoursesAndSignDocs
    });

    // Merge SignDocs into organizations (if withCoursesAndSignDocs is true)
    const organizations = useMemo(() => {
        if (!adminOrgs) return undefined;

        // If SignDocs not requested, return orgs as-is
        if (!withCoursesAndSignDocs) {
            return adminOrgs;
        }

        // If SignDocs requested but not yet loaded, wait for both queries to complete
        // This prevents returning partial data (orgs without signDocs property)
        if (!uniqueSignDocs) {
            return undefined;
        }

        // Clone and merge SignDocs into organizations
        return adminOrgs.map(org => {
            const orgId = get(org, 'organization.id');
            const orgSignDocs = filter(
                map(uniqueSignDocs, signDoc => ({
                    ...signDoc,
                    organizationSignDoc: filter(
                        signDoc?.organizationSignDoc,
                        osd => osd?.OrganizationId === orgId
                    )
                })),
                signDoc => signDoc?.organizationSignDoc?.length > 0
            );

            return {
                ...org,
                organization: {
                    ...org.organization,
                    signDocs: orgSignDocs
                }
            };
        });
    }, [adminOrgs, uniqueSignDocs, withCoursesAndSignDocs]);

    // Build organization options (for dropdowns)
    const organizationOptions = useMemo(() => {
        if (!organizations) return undefined;

        // Filter out VPO
        const orgWithoutVPO = filter(organizations, org => !get(org, 'organization.isPersonal'));

        let orgOptions = map(orgWithoutVPO, org => ({
            value: get(org, 'organization.id'),
            orgLogo: get(org, 'organization.orgLogo'),
            text: get(org, 'organization.orgName')
        }));

        // Add "All" option if requested
        if (withAllOption) {
            orgOptions.unshift({value: 'all', text: i18n.translate('allOrganizations')});
        }

        return orgOptions;
    }, [organizations, withAllOption, i18n]);

    // Combined loading state
    const isLoading = isLoadingOrgs || (withCoursesAndSignDocs && isLoadingSignDocs);

    /**
     * Trigger reload of organizations data (refetches React Query cache).
     * Uses Promise.all to ensure both queries complete together,
     * preventing intermediate renders with mismatched data.
     * @returns {Promise<void>}
     */
    const triggerReloadOrganizations = async () => {
        const refetchPromises = [refetchOrgs()];
        if (withCoursesAndSignDocs) {
            refetchPromises.push(refetchSignDocs());
        }
        await Promise.all(refetchPromises);
    };

    return {
        adminOrganizationOptions: organizationOptions,
        adminOrganizations: organizations,
        isOrgsLoading: isLoading,
        triggerReloadOrganizations
    };
};
